import React, { memo, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormattedCandidate } from 'lib/CandidatePortraits/CandidatePortraits';
import ApiContext from 'api/ApiContext';
import PortraitCloudTooltip, { PortraitPosition } from './PortraitCloudTooltip';

interface Props {
  electedCandidatesCanton: FormattedCandidate[];
}

// memoized to prevent flickering images/avatars
const PortraitCloud = ({ electedCandidatesCanton }: Props) => {
  const { t } = useTranslation('wap');

  const { apiUrl } = useContext(ApiContext);

  const [hoveredCandidate, setHoveredCandidate] = useState<FormattedCandidate>(undefined);
  const [portraitPosition, setPortraitPosition] = useState<PortraitPosition>({
    bottom: 0,
    left: 0,
  });
  const [showTooltip, setShowTooltip] = useState(false);

  const queryRoot =
    document.querySelector(`[data-${process.env.REACT_APP_NAME}]`).shadowRoot ?? document;

  const handleOnCandidateHover = (candidate: FormattedCandidate) => () => {
    setShowTooltip(true);
    setHoveredCandidate(candidate);
    const hoveredElement = queryRoot.getElementById(`portrait-${candidate.id}`);
    const { bottom, left, right } = hoveredElement.getBoundingClientRect();
    setPortraitPosition({ bottom, right, left });
  };

  const handleOnCandidateLeave = () => () => {
    setShowTooltip(false);
  };

  const handleCandidateImageError = (
    e: React.SyntheticEvent<HTMLImageElement, Event>,
  ) => {
    e.currentTarget.src = `${process.env.PUBLIC_URL}/images/missingportrait_placeholder.svg`;
  };

  const getAltText = (candidate: FormattedCandidate) => {
    if (candidate.photoPath) {
      return `${t('portrait')} ${candidate.firstName} ${candidate.lastName}`;
    }

    return `${t('portrait_no_image')}`;
  };

  const getStyle = (candidate: FormattedCandidate) => {
    return {
      '--color-portraitcloud-border': `${candidate.partyColor}`,
    } as React.CSSProperties;
  };

  return (
    <>
      <ul className="portraitcloud" aria-hidden>
        {electedCandidatesCanton?.map((candidate) => {
          return (
            <li key={candidate.id} className="portraitcloud__item">
              <img
                style={getStyle(candidate)}
                onMouseEnter={handleOnCandidateHover(candidate)}
                onMouseLeave={handleOnCandidateLeave()}
                id={`portrait-${candidate.id}`}
                src={`${apiUrl}${candidate.photoPath}`}
                alt={getAltText(candidate)}
                className="portraitcloud__portrait"
                onError={handleCandidateImageError}
              />
            </li>
          );
        })}
      </ul>
      <PortraitCloudTooltip
        hoveredCandidate={hoveredCandidate}
        portraitPosition={portraitPosition}
        showTooltip={showTooltip}
      />
      <p className="portraitcloud__screenreaderinfo">
        {t('portraits_screen_reader_info')}
      </p>
    </>
  );
};

const arePropsEqual = (prevProps: Props, nextProps: Props) => {
  return (
    prevProps.electedCandidatesCanton?.length ===
    nextProps.electedCandidatesCanton?.length
  );
};

export default memo(PortraitCloud, arePropsEqual);
